import type { User } from '@/models'
import { parseWithDate } from '@/utilities'

const STORAGE_KEY = 'user'
const STORAGE_VERSION = 1

interface StorageData {
  version: number
  data: User
  timestamp: number
}

/**
 * Custom JSON replacer to handle circular references in Resource objects
 * Excludes the 'parent' field which can create circular references
 */
const projectReplacer = (key: string, value: unknown) => {
  if (key === 'parent') return undefined
  return value
}

/**
 * Validates that a user has all required fields
 */
function validateUser(user: unknown): boolean {
  return (
    user !== null &&
    typeof user === 'object' &&
    'id' in user &&
    'projects' in user &&
    Array.isArray(user.projects) &&
    'likedResources' in user &&
    Array.isArray(user.likedResources) &&
    'likedCases' in user &&
    Array.isArray(user.likedCases)
  )
}

/**
 * Serializes user to JSON string with versioning
 */
function serializeUser(user: User): string {
  const data: StorageData = {
    version: STORAGE_VERSION,
    data: user,
    timestamp: Date.now(),
  }
  return JSON.stringify(data, projectReplacer)
}

/**
 * Deserializes JSON string to user with validation
 * Returns undefined if data is invalid or corrupted
 */
function deserializeUser(jsonString: string): User | undefined {
  try {
    const parsed = parseWithDate<StorageData>(jsonString)

    // Validate structure
    if (!parsed || typeof parsed !== 'object' || !parsed.data) {
      console.warn('Invalid user structure in localStorage')
      return undefined
    }

    // Validate user has required fields
    if (!validateUser(parsed.data)) {
      console.warn('User data in localStorage is missing required fields')
      return undefined
    }

    return parsed.data as User
  } catch (error) {
    console.error('Failed to deserialize user:', error)
    return undefined
  }
}

/**
 * Loads user from localStorage
 * Returns undefined if no data exists or if data is corrupted
 */
export function loadUserFromStorage(): User | undefined {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return undefined
    }

    const user = deserializeUser(stored)
    return user
  } catch (error) {
    console.error('Failed to load user from localStorage:', error)
    // Clear corrupted data
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Ignore errors during cleanup
    }
    return undefined
  }
}

/**
 * Saves user to localStorage
 * Returns true if successful, false if quota exceeded or other error
 */
export function saveUserToStorage(user: User): boolean {
  try {
    const serialized = serializeUser(user)
    localStorage.setItem(STORAGE_KEY, serialized)
    return true
  } catch (error) {
    if (error instanceof DOMException && error.code === 22) {
      // QuotaExceededError
      console.error('localStorage quota exceeded. User not persisted.')
      return false
    }
    console.error('Failed to save user to localStorage:', error)
    return false
  }
}

/**
 * Clears user data from localStorage
 */
export function clearUserFromStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error('Failed to clear user from localStorage:', error)
  }
}
